<template>
    <ExternalUserActionComponent dialog-id="wishlist-edition" open-dialog-event="wishlist:edit-mode" size="default">
        <template #edit-header>{{ t('wishlist.modal.title', {0: profile!.firstname}) }}</template>
        <template #edit-body>
            <VJoyButton icon="add" variant="secondary" @click="openAddWishlist">
                {{ t('wishlist.modal.create.new') }}
            </VJoyButton>

            <VJoyWrapper
                v-for="wishlist in wishlists.items"
                :key="wishlist.id"
                justify="flex-start"
                item-stretch
                class="wishlist"
                no-margin
                @click="addOrRemoveFromWishlist(wishlist)"
            >
                <div>
                    <div>
                        <strong>{{ wishlist.name }} ({{ wishlist.profiles.length }})</strong>
                    </div>
                    <div class="wishlist__creator">
                        {{ t('wishlist.modal.list.owner') }}
                        <strong>{{ wishlist.ownerFirstname }}</strong>
                    </div>
                </div>
                <VJoyIcon class="wishlist__star" :name="starFillIfProfileInsideWishlist(wishlist)" size="medium"></VJoyIcon>
            </VJoyWrapper>
        </template>
    </ExternalUserActionComponent>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {useTranslation, useProfileTracking} from '#imports';
    import type {TJoyIconsNames} from '@maltjoy/core-vue';
    import {pushVJoySnackbar, VJoyButton, VJoyIcon, VJoyWrapper} from '@maltjoy/core-vue';
    import ExternalUserActionComponent from '~/components/external-user-action/ExternalUserActionComponent.vue';
    import {eventBus} from '~/config/events';
    import {useProfileStore} from '~/store/profileStore';
    import {useWishlistStore} from '~/store/wishlist/wishlistStore';
    import type {Wishlist} from '~/types/domain/Wishlist';

    const {t} = useTranslation();
    const {profile} = storeToRefs(useProfileStore());
    const {wishlists} = storeToRefs(useWishlistStore());
    const {addToWishlist, removeFromWishlist} = useWishlistStore();
    const {trackFreelancerAddedToWishList, trackFreelancerRemovedFromWishList} = useProfileTracking();

    async function addOrRemoveFromWishlist(wishlist: Wishlist): Promise<void> {
        if (wishlist.profiles.includes(profile.value!.profileId)) {
            await removeFromWishlist(wishlist.id, profile.value!);
            trackFreelancerRemovedFromWishList(profile.value?.accountId).then(/* no need to await */);
            pushVJoySnackbar({
                props: {
                    message: t('wishlist.profile.removal.success.message'),
                    level: 'success',
                },
            });
        } else {
            await addToWishlist(wishlist.id, profile.value!);
            trackFreelancerAddedToWishList(profile.value?.accountId).then(/* no need to await */);
            pushVJoySnackbar({
                props: {
                    message: t('wishlist.saved.label'),
                    level: 'success',
                },
            });
        }
    }

    function starFillIfProfileInsideWishlist(wishlist: Wishlist): TJoyIconsNames {
        return wishlist.profiles.includes(profile.value!.profileId) ? 'star-fill' : 'star';
    }

    function openAddWishlist() {
        eventBus.emit('wishlist:add-mode');
    }
</script>

<style lang="scss" scoped>
    .wishlist {
        cursor: pointer;
        border-radius: var(--joy-core-radius-3);
        padding: 20px;

        &:hover {
            background-color: var(--joy-color-secondary-10);
        }

        &__creator {
            font-size: var(--joy-font-size-primary-300);
        }

        &__lock {
            flex: 0;
        }

        &__star {
            justify-content: flex-end;
        }
    }
</style>
